/* eslint-disable react/jsx-props-no-spreading */
import mergeClasses from '@srcDS/js/utils/shallowMerge';
import { useDevicesScreen } from '@srcDS/js/utils/useDevicesScreen';
import { Link } from '@srcDS/js/components/atoms/link/Link';
import SwiperWithArrows from '@srcDS/js/components/molecules/swiperWithArrows/SwiperWithArrows';
import ProductTile from '@srcDS/js/components/organisms/productTile';
import { Heading } from '@srcDS/js/components/typography/heading/Heading';
import { Text } from '@srcDS/js/components/typography/text/Text';
import Title from '@srcDS/js/components/typography/title';
import defaultClasses from './ProductListSlider.module.scss';
import MediaImage from '@frontastic/catwalk/src/js/mediaImage';
import clsx from 'clsx';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Scrollbar, Autoplay, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useSelector } from 'react-redux';
import { pricesForGroup, findDiscount } from '@srcDS/js/utils/helpers';
import { getPrices } from '@apgnext/frontend/legacy/js/utils/helpers';

const ProductListSlider = ({
  products,
  label,
  title,
  titleAlign,
  xlOffsets = 0,
  lgOffsets = 0,
  mdOffsets = 0,
  smOffsets = 0,
  xlSlidesPerView,
  lgSlidesPerView,
  mdSlidesPerView,
  smSlidesPerView,
  scrollbarEl = null,
  wishlist,
  links,
  featuredImage,
  customClasses,
  hideArrows = false,
  placement = 'home',
  scrollBarVisibility = 'always', // 'mobile', 'always', 'hide'
  type,
  listName,
  swiperWithArrows,
  centerInsufficientSlides,
  nostoPlacement,
  nostoCallback,
  enableTouchOnMobile = true,
  gridType,
}) => {

  const { isMobile, isTablet, isDesktop, isLargeDesktop } = useDevicesScreen();
  const { loggedIn, customerGroupId } = useSelector((state) => {
    return {
      loggedIn: state.app.context.session?.loggedIn,
      customerGroupId: state.app.context.session?.account?.projectSpecificData?.customerGroupId ?? null,
    };
  });

  const TYPES = {
    related: 'related-products',
    recently: 'recently-viewed',
    recommendations: 'product-recommendations',
  };

  const styles = mergeClasses(defaultClasses, customClasses);
  const swiperRef = useRef(null);

  const productLength = products.length;

  const defaultCenterMode =
    (isMobile && productLength <= smSlidesPerView) ||
    (isTablet && productLength <= mdSlidesPerView) ||
    (isDesktop && productLength <= lgSlidesPerView) ||
    (isLargeDesktop && productLength <= xlSlidesPerView);

  const [isCenterMode, setCenterMode] = useState(defaultCenterMode);

  useLayoutEffect(() => {
    setCenterMode(
      (isMobile && productLength <= smSlidesPerView) ||
        (isTablet && productLength <= mdSlidesPerView) ||
        (isDesktop && productLength <= lgSlidesPerView) ||
        (isLargeDesktop && productLength <= xlSlidesPerView),
    );
  }, [isTablet, isDesktop, isLargeDesktop]);

  const slidesOffset = {
    [isLargeDesktop]: xlOffsets,
    [isDesktop]: lgOffsets,
    [isTablet]: mdOffsets,
    [isMobile]: smOffsets,
  }[true];

  useLayoutEffect(() => {
    if ((mdSlidesPerView && lgSlidesPerView && xlSlidesPerView) || !swiperRef.current || !slidesOffset) return;

    const { virtualSize } = swiperRef.current;
    const { innerWidth } = window;

    // swiper.js has weird behavior if offsets set - even if it has only 1 slide
    // and empty space the total width will be more than screen width and offset
    // goes out of screen
    const offsetOutOfScreen = virtualSize === innerWidth + slidesOffset;

    // swiperRef.current.virtualSize variable often has out of date value
    // because translate3d is in progress on rendering stage and react doesn't
    // rerender component after translation completed and swiper recalculate
    // virtualSize
    const isNotTranslated = virtualSize === innerWidth;

    setCenterMode(isNotTranslated || offsetOutOfScreen);
  }, [slidesOffset, swiperRef]);

  const mediaImageSizeProps = {
    [isLargeDesktop]: {
      width: 1208,
      height: 986,
    },
    [isDesktop]: {
      width: 680,
      height: 640,
    },
    [isTablet]: {
      width: 438,
      height: 508,
    },
    [isMobile]: {
      width: 335,
      height: 315,
    },
  }[true];

  const wishlistItems = wishlist?.data?.lineItems?.reduce((acc, item) => acc.set(item.variant.sku, item), new Map());

  const isFeatured = !!featuredImage?.media;
  const isArticlePage = placement === 'article';

  const breakpoints = {
    320: {
      spaceBetween: 12,
      slidesPerView: smSlidesPerView,
      centeredSlides: false,
    },
    680: {
      spaceBetween: 12,
      slidesPerView: mdSlidesPerView,
      centeredSlides: false,
    },
    1120: {
      spaceBetween: type === TYPES.related ? 25 : type === TYPES.recommendations ? 20 : 16,
      slidesPerView: lgSlidesPerView,
      centeredSlides: false,
    },
    1920: {
      spaceBetween: isArticlePage ? 25 : type === TYPES.related ? 54 : type === TYPES.recommendations ? 20 : 16,
      slidesPerView: xlSlidesPerView,
      centeredSlides: false,
    },
  };

  const categoriesTitle =
    label || title ? (
      <div
        className={clsx(styles.titleWrapper, {
          [styles.wrapperWithFeatured]: isFeatured,
          [styles[`title_${titleAlign}`]]: titleAlign,
          [styles.titleCenterMode]: isCenterMode,
        })}
      >
        <Title className={styles.widgetTitle} align={titleAlign}>
          {label && (
            <Text className={styles.widgetLabel} as="span" textTransform="uppercase">
              {label}
            </Text>
          )}
          {title && (
            <Heading
              className={styles[`title-${placement}`]}
              align={titleAlign}
              as="h2"
              placement="common"
              textTransform="uppercase"
              weight="normal"
            >
              {title}
            </Heading>
          )}
        </Title>
        {links && links.length && (
          <div className={styles.linkWrapper}>
            {links.map((link, index) => (
              <Link key={index} link={link} ariaLabel={link.linkTitle}>
                <span className={styles.link}>{link.linkTitle || ''}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    ) : null;

  const SwiperComponent = swiperWithArrows ? SwiperWithArrows : isMobile || !isFeatured ? Swiper : SwiperWithArrows;

  // reset scroll position on orientation change
  useEffect(() => {
    const handleResize = () => {
      if (swiperRef && swiperRef?.current) {
        setTimeout(() => {
          swiperRef.current.update();
          swiperRef.current.slideTo(0, 0);
        }, 300);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const slider = useMemo(() => {
    let swiperProps = {
      onSwiper: (swiper) => {
        swiperRef.current = swiper;
      },
      className: clsx(styles.swiperContainer, {
        [styles.featured]: isFeatured,
        [styles.centerMode]: isCenterMode,
      }),
      breakpoints: breakpoints,
      centeredSlidesBounds: true,
      centerInsufficientSlides,
      modules: [Scrollbar, Autoplay],
      scrollbar: {
        el: scrollbarEl ? `.${scrollbarEl}` : '.swiper-scrollbar',
        draggable: true,
        dragSize: 62,
      },
      slidesOffsetAfter: !isCenterMode && slidesOffset,
      slidesOffsetBefore: !isCenterMode && slidesOffset,
      observer: true,
      observeParents: true,
      onObserverUpdate: (swiper) => {
        swiper.enabled = !isCenterMode;
        swiper.params.scrollbar.hide = false;
      },
      watchOverflow: true,
    };

    if (SwiperComponent.displayName !== 'Swiper') {
      swiperProps = {
        ...swiperProps,
        autoHeightSelector: (slideNode) => slideNode?.firstChild?.firstChild,
      };
    }

    return (
      <SwiperComponent {...swiperProps}>
        {products
          ? Array.from(products).map((product, index) => {
              const {
                productId,
                skus,
                variants,
                images,
                productName,
                tags,
                label,
                projectSpecificData: { prices, vipPrices, discounts },
                url,
              } = product;

              const variantList = variants || skus;

              const wishlistItem = skus?.reduce((acc, sku) => acc ?? wishlistItems?.get(sku.name), null);

              const {
                price: regularPrice,
                discountedPrice,
                onSale,
              } = placement === 'article' || placement === 'productOutfit'
                ? getPrices(prices)
                : pricesForGroup(prices, vipPrices, loggedIn, customerGroupId);

              const promoContent =
                placement === 'article' || placement === 'productOutfit'
                  ? product?.projectSpecificData?.discount
                  : findDiscount(discounts, {
                      loggedIn,
                      vipPrices,
                      onSale,
                      customerGroupId,
                    });

              const productItem = {
                ...variantList?.[0],
                sku: variantList[0]?.name,
                attributes: {
                  ...variantList?.[0].attributes,
                },
              };

              const imagesArr = type === 'completeOutfit' ? variantList?.[0]?.images : images;

              return (
                <SwiperSlide key={index} className={styles.swiperSlide}>
                  <ProductTile
                    parentProduct={product}
                    item={productItem}
                    promoContent={promoContent}
                    images={imagesArr}
                    productName={productName || product.name}
                    tags={tags}
                    label={label}
                    remoteImage={false}
                    productId={productId}
                    regularPrice={regularPrice}
                    discountedPrice={discountedPrice}
                    onSale={onSale}
                    url={url || product?._url}
                    inWishlist={!!wishlistItem}
                    hideArrows={hideArrows}
                    position={index}
                    variants={variants}
                    placement={listName || title}
                    theme="Sportscraft"
                    nostoPlacement={nostoPlacement}
                    nostoCallback={nostoCallback}
                    gridType={gridType}
                    enableGallery={true}
                    enableTouchOnMobile={enableTouchOnMobile}
                    finalSale={
                      productItem?.attributes?.finalsale === 'true' || productItem?.attributes?.finalsale === true
                    }
                  />
                </SwiperSlide>
              );
            })
          : null}
        <div
          tabIndex="0"
          className={`swiper-scrollbar js-swiper-scrollbar
          ${styles.scrollbar}
          ${styles.customScrollbar}
          ${styles[`scrollbar-${scrollBarVisibility}`]}
          ${scrollbarEl}
          `}
        />
      </SwiperComponent>
    );
  }, [
    isMobile,
    isTablet,
    isDesktop,
    isLargeDesktop,
    smSlidesPerView,
    xlSlidesPerView,
    lgSlidesPerView,
    mdSlidesPerView,
    products,
    wishlistItems,
    enableTouchOnMobile,
    type,
    isCenterMode,
    isFeatured,
    hideArrows,
    scrollBarVisibility,
    nostoCallback,
    nostoPlacement,
  ]);

  return (
    <div
      className={clsx(styles.widgetSection, styles[`section-${placement}`], styles[`type-${type}`], {
        [styles.isFeatured]: isFeatured && !isMobile,
      })}
      style={{
        '--smOffsets': `${smOffsets}px`,
        '--mdOffsets': `${mdOffsets}px`,
        '--lgOffsets': `${lgOffsets}px`,
        '--xlOffsets': `${xlOffsets}px`,
        '--scrollOffset': `${slidesOffset}px`,
      }}
    >
      {isFeatured && <MediaImage {...mediaImageSizeProps} media={featuredImage} className={styles.featuredMedia} />}
      {isMobile && categoriesTitle}
      <div
        className={clsx(styles.widgetCarousel, {
          [styles.featured]: isFeatured && !isMobile,
        })}
      >
        {!isMobile && categoriesTitle}
        {slider}
      </div>
    </div>
  );
};

export default ProductListSlider;
